import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';

// LaTeX 分隔符归一（remark-math 只认 $/$$）：
// LLM 偶发 \[...\] \(...\)；OCR 表格输出的裸 tabular 需要 $$ 包裹；
// 旧数据的行尾 \\ 噪声要清掉——但 tabular 行分隔符合法，故先把表格抽出来再清洗其余文本
function normalizeMath(text: string): string {
  let t = String(text || '');
  const tables: string[] = [];
  // 表格（tabular/array）先抽出保护——array 的行分隔符 \\ 在行尾，不能被噪声清除误伤
  t = t.replace(/\\begin\{(tabular|array)\}[\s\S]*?\\end\{\1\}/g, (m) => {
    tables.push(m);
    return `%%TAB${tables.length - 1}%%`;
  });
  // 行尾反斜杠噪声（1-2 个 \ + 尾随空格 → 换行）
  t = t.replace(/\\{1,2}[ \t]*(?=\n)/g, '');
  // 分隔符归一
  t = t.replace(/\\\[([\s\S]*?)\\\]/g, (_, m) => `$$${m}$$`);
  t = t.replace(/\\\(([\s\S]*?)\\\)/g, (_, m) => `$${m}$`);
  // 已被 $$ 包裹的表格先解包，防重复包裹
  t = t.replace(/\$\$\s*%%TAB(\d+)%%\s*\$\$/g, '%%TAB$1%%');
  // 还原：KaTeX 只支持 array（tabular 报 \hline 错）；单元格内 $ 非法一并去除；
  // 内部换行折叠为空格——remark-math v6 的多行 $$ 块会把内容搞坏，单行可靠（LaTeX 空白不敏感）
  t = t.replace(/%%TAB(\d+)%%/g, (_, i) => {
    const tab = tables[Number(i)]
      .replace(/\$/g, '')
      .replace(/\\begin\{tabular\}/g, '\\begin{array}')
      .replace(/\\end\{tabular\}/g, '\\end{array}')
      .replace(/\r?\n/g, ' ');
    return `$$\n${tab}\n$$`;
  });
  // 其余多行 $$ 展示公式（如解题输出）同样折叠为单行
  t = t.replace(/\$\$([^$]*)\$\$/g, (_, m) => `$$\n${String(m).replace(/\r?\n/g, ' ')}\n$$`);
  return t;
}

// LLM 输出的 Markdown 渲染（总评/讲解/建议/批注等）。
// 卡片内嵌场景：标题压到 3 级尺度（LLM 常给 # 一级大标题），正文 prose 间距。
export default function Markdown({ text, className = '' }: { text: string; className?: string }) {
  return (
    <div className={`leading-relaxed text-gray-800 [&_a]:text-brand-600 [&_a]:underline ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          table: ({ children }) => (
            <div className="my-3 overflow-x-auto">
              <table className="w-full text-xs border-collapse">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
          th: ({ children }) => <th className="border border-gray-200 px-2.5 py-1.5 text-left font-semibold text-gray-700">{children}</th>,
          td: ({ children }) => <td className="border border-gray-200 px-2.5 py-1.5 align-top">{children}</td>,
          h1: ({ children }) => <div className="text-base font-bold text-gray-900 mt-4 mb-2 first:mt-0">{children}</div>,
          h2: ({ children }) => <div className="text-base font-semibold text-gray-900 mt-4 mb-2 first:mt-0">{children}</div>,
          h3: ({ children }) => <div className="text-sm font-semibold text-gray-900 mt-3 mb-1.5 first:mt-0">{children}</div>,
          h4: ({ children }) => <div className="text-sm font-semibold text-gray-800 mt-3 mb-1 first:mt-0">{children}</div>,
          p: ({ children }) => <p className="my-2 first:mt-0 last:mb-0">{children}</p>,
          strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
          ul: ({ children }) => <ul className="my-2 ml-5 list-disc space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="my-2 ml-5 list-decimal space-y-1">{children}</ol>,
          li: ({ children }) => <li className="pl-1">{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className="my-2 pl-3 border-l-2 border-brand-300 text-gray-600">{children}</blockquote>
          ),
          hr: () => <hr className="my-4 border-gray-200" />,
          code: ({ children }) => (
            <code className="px-1 py-0.5 rounded bg-gray-100 text-[13px] font-mono text-gray-700">{children}</code>
          ),
        }}
      >
        {normalizeMath(text || '')}
      </ReactMarkdown>
    </div>
  );
}
